import React, { useState } from 'react';
import { Calculator } from '../../types';
import { CalculatorShell } from './CalculatorShell';
import { AllCalculatorsModule } from './AllCalculatorsModule';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';

// Import individual calculators
import ArithmeticMeanCalculator from './math/ArithmeticMeanCalculator';
import WeightedMeanCalculator from './math/WeightedMeanCalculator';
import UnitConverter from './math/UnitConverter';
import PercentageCalculator from './finance/PercentageCalculator'; // Reuse from finance
import CompoundRuleOfThreeCalculator from './math/CompoundRuleOfThreeCalculator';
import RatioProportionCalculator from './math/RatioProportionCalculator';
import PercentageChangeCalculator from './math/PercentageChangeCalculator';

interface MathModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const MathModule: React.FC<MathModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const supportedSlugs = [
    'media-aritmetica',
    'media-ponderada',
    'porcentagem',
    'conversoes',
    'regra-de-tres-composta',
    'razao-proporcao',
    'variacao-percentual'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderCalculator(calculator.slug, (data) => setExportData({ title: calculator.title, results: data }))}
    </CalculatorShell>
  );
};

function renderCalculator(slug: string, onResultChange: (data: { label: string, value: string }[]) => void) {
  switch (slug) {
    case 'media-aritmetica': return <ArithmeticMeanCalculator onResultChange={onResultChange} />;
    case 'media-ponderada': return <WeightedMeanCalculator onResultChange={onResultChange} />;
    case 'porcentagem': return <PercentageCalculator onResultChange={onResultChange} />;
    case 'conversoes': return <UnitConverter onResultChange={onResultChange} />;
    case 'regra-de-tres-composta': return <CompoundRuleOfThreeCalculator onResultChange={onResultChange} />;
    case 'razao-proporcao': return <RatioProportionCalculator onResultChange={onResultChange} />;
    case 'variacao-percentual': return <PercentageChangeCalculator onResultChange={onResultChange} />;
    default: return null;
  }
}
